import React, { useEffect, useState } from "react";
import { fetchPopulation, fetchReportResources } from "../../services/httpClient";
import html2canvas from "html2canvas";
import { ButtonGroup, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { ResourcesMetric } from "./charts-metrics/ResourcesMetric";
import { formatDateTimeToLocal, getCurrentDateTime } from "../../helpers/date";
import { signal } from "@preact/signals-react";
import { CSVLink } from "react-csv";
import { ResourceAssignmentPieChart } from "./charts-metrics/ResourceAssignmentPieChart";
import { ResourcesAssignedTable } from "./charts-metrics/ResourcesAssignedTable";

export const showResources = signal(true);

const ResourcesTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [resourceData, setResourceData] = useState<any>([]);
  const [populationData, setPopulationData] = useState<any>([]);
  const [reportRunning, setReportRunning] = useState<boolean>(false);

  const defaultCSVHeaders = [
    { label: "Resource ID", key: "resource_id", hide: true },
    { label: "Resource", key: "resource_name" },
    { label: "Resource Type", key: "resource_type_key", hide: true },
    { label: "Resource Category", key: "resource_category" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Name", key: "assignee_name" },
    { label: "Assigned Date", key: "assigned_at" },
    { label: "Returned Date", key: "returned_at" },
  ];

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
    getData(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
  }, []);

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const getData = (start: string, end: string) => {
    getResourceAssignments(start, end);
    getPopulation(start, end);
  };

  const getResourceAssignments = (start: string, end: string) => {
    fetchReportResources(start, end)
      .then((res: any) => {
        const resources = res[0].data;
        setResourceData(resources);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getPopulation = (start: string, end: string) => {
    fetchPopulation(start, end)
      .then((res: any) => {
        const population = res[0].data;
        setPopulationData(population);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClickDownloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("resources-section");
    const reportTitle = document.getElementById("resources-title");
    const refreshButton = document.getElementById("resources-refresh-button");
    const downloadConsumablesButton = document.getElementById("download-consumables-button");
    const downloadNonConsumablesButton = document.getElementById("download-nonconsumables-button");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }
      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (downloadConsumablesButton) downloadConsumablesButton.style.visibility = "hidden";
      if (downloadNonConsumablesButton) downloadNonConsumablesButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
      if (refreshButton) refreshButton.style.visibility = "visible";
      if (downloadConsumablesButton) downloadConsumablesButton.style.visibility = "visible";
      if (downloadNonConsumablesButton) downloadNonConsumablesButton.style.visibility = "visible";
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <div id="resources-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="resources-title" className="mb-2">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Resources</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Dropdown as={ButtonGroup}>
                  <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Downloads
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="super-colors">
                    <Dropdown.Item eventKey="1" onClick={() => onClickDownloadReport()}>
                      Report
                    </Dropdown.Item>
                    <Dropdown.Item
                      eventKey="2"
                      as={CSVLink}
                      data={resourceData ? resourceData : [{}]}
                      headers={defaultCSVHeaders}
                      filename={`resources_${getCurrentDateTime()}.csv`}
                      target="_blank"
                    >
                      Data Export
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
          </Row>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getData}
            hideBtnId="resources-refresh-button"
          />
          <Row className="mt-3 mb-5">
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="people" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="resources" resourceType="consumable" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="resources" resourceType="nonconsumable" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="usage" population={populationData} />
            </Col>
          </Row>
          <ResourceAssignmentPieChart data={resourceData} />
          <ResourcesAssignedTable data={resourceData} />
          {/* <ResourcesAssignedByTypeTable data={consumableTableData} resourceType="consumable" />
          <ResourcesAssignedByTypeTable data={nonConsumableTableData} resourceType="nonconsumable" /> */}
        </>
      )}
    </div>
  );
};

export default ResourcesTab;
